/**
 * Lo que el analista está escribiendo, guardado mientras escribe.
 *
 * Las sesiones duran doce horas, y vencen cuando vencen: en medio de una revisión, con una decisión
 * a medio redactar. Todo lo demás que hay en pantalla se puede volver a pedir al servidor — el
 * fundamento que esa persona estaba escribiendo, no. Es lo único de la aplicación que, si se pierde,
 * no se recupera de ningún lado.
 *
 * Por eso vive en `localStorage` y no en memoria: sobrevive a que la sesión venza, a un F5 y a que
 * el navegador se cierre solo. Se borra al guardar, que es cuando deja de ser un borrador. Y cada
 * acceso está envuelto: en una ventana privada, o con el almacenamiento bloqueado por política del
 * banco, `localStorage` lanza, y perder el borrador es malo pero romper la pantalla es peor.
 */

const PREFIX = "bancor.draft.";

export function loadDraft<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

export function saveDraft(key: string, value: unknown): void {
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    /* sin almacenamiento el borrador no sobrevive a la sesión; la pantalla sigue funcionando */
  }
}

export function clearDraft(key: string): void {
  try {
    localStorage.removeItem(PREFIX + key);
  } catch {
    /* nada que hacer, y nada que romper */
  }
}

/** Si vale la pena avisar que hay algo guardado: un borrador vacío no es un borrador. */
export function hasContent(draft: Record<string, unknown> | null): boolean {
  if (!draft) return false;
  return Object.values(draft).some((value) => typeof value === "string" && value.trim().length > 0);
}
